import React from "react";
import { Link } from "react-router-dom";
import { useSEO } from "@/lib/useSEO";

export default function PrivacyPolicy() {
  useSEO({
    title: "Privacy Policy — Maqam",
    description: "How Maqam handles your personal data, Google Gmail API usage, token storage, and how to disconnect. Compliant with Saudi PDPL and GDPR.",
    path: "/privacy-policy",
  });
  return (
    <div className="min-h-screen bg-[#1a1a1c] text-white" data-testid="privacy-policy-page">
      <header className="border-b border-white/10">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-1">
            <span className="font-display font-black text-xl tracking-tighter inline-flex items-center">
              <span className="border border-white px-1.5 py-0.5 mr-[2px] leading-none">M</span>
              <span>aqam</span>
            </span>
          </Link>
          <Link to="/" className="text-sm text-white/60 hover:text-white">← Back home</Link>
        </div>
      </header>

      <main className="max-w-[820px] mx-auto px-6 md:px-10 py-16 space-y-10">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-white/50 mb-3">/ Legal</div>
          <h1 className="font-display font-normal text-4xl md:text-5xl tracking-tight">Privacy Policy</h1>
          <p className="text-white/50 text-sm mt-3">Last updated: 25 April 2026 · Aligned with Saudi PDPL (2023)</p>
        </div>

        <section className="space-y-4 text-white/80 leading-relaxed">
          <p>
            Maqam (<span className="font-mono">maqam-ksa.com</span>) is operated under the laws of the
            Kingdom of Saudi Arabia. This policy explains what personal data we collect, why, and the
            rights you have under the Personal Data Protection Law (PDPL).
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display font-bold text-2xl">1. Data we collect</h2>
          <ul className="list-disc ps-6 space-y-2 text-white/75">
            <li>Account data: full name, email, hashed password, language preference.</li>
            <li>Profile data: CV text, uploaded documents (CV, motivation letter, diploma, certificates, profile picture), skills, target job titles, preferred cities, salary expectation.</li>
            <li>Application data: job IDs you applied to, AI-generated cover letters, employer email targets.</li>
            <li>Authentication tokens: Google OAuth refresh tokens (only when you connect Gmail to send applications natively).</li>
            <li>Usage metrics: bulk-apply quota, last login, device/browser metadata.</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="font-display font-bold text-2xl">2. Why we collect it</h2>
          <ul className="list-disc ps-6 space-y-2 text-white/75">
            <li>To match you with relevant Saudi & Gulf jobs (via Claude Sonnet 4.5).</li>
            <li>To send tailored cover letters + your CV to employers on your behalf — only with your explicit per-session consent.</li>
            <li>To provide a private platform inbox (<span className="font-mono">yourname@maqam-ksa.com</span>) so your real email stays hidden.</li>
            <li>To send saved-search digests via Resend when new matching jobs appear.</li>
            <li>To bill paid subscriptions via Stripe or Moyasar (we never store card numbers).</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="font-display font-bold text-2xl">3. Your PDPL rights</h2>
          <ul className="list-disc ps-6 space-y-2 text-white/75">
            <li><b>Access</b> — download all your data as JSON from <Link to="/app/privacy" className="link-underline text-white">your privacy dashboard</Link>.</li>
            <li><b>Correction</b> — edit your profile at any time.</li>
            <li><b>Erasure</b> — delete your account permanently. All data is removed from our database.</li>
            <li><b>Withdraw consent</b> — revoke bulk-apply consent at any time. Past sent applications cannot be recalled (already delivered).</li>
            <li><b>Portability</b> — exports are machine-readable JSON.</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="font-display font-bold text-2xl">4. Third-party processors</h2>
          <p className="text-white/75">We share strictly necessary data with:</p>
          <ul className="list-disc ps-6 space-y-2 text-white/75">
            <li><b>Anthropic (Claude Sonnet 4.5)</b> — anonymized job text + your skills for AI ranking & cover-letter writing.</li>
            <li><b>Google (Gmail OAuth)</b> — only the <span className="font-mono">gmail.send</span> scope; we cannot read or modify your inbox.</li>
            <li><b>Resend</b> — outbound transactional emails on your behalf.</li>
            <li><b>RapidAPI (JSearch, LinkedIn)</b> — public job listings (we send no personal data, only search keywords).</li>
            <li><b>Stripe / Moyasar</b> — payment tokens for paid tiers.</li>
            <li><b>MongoDB Atlas</b> — encrypted-at-rest data storage in EU/MENA regions.</li>
          </ul>
        </section>

        <section className="space-y-3" data-testid="google-api-services-disclosure">
          <h2 className="font-display font-bold text-2xl">5. Google API Services disclosure</h2>
          <p className="text-white/75">
            Maqam's use and transfer to any other app of information received from Google APIs will
            adhere to the{" "}
            <a
              href="https://developers.google.com/terms/api-services-user-data-policy"
              target="_blank"
              rel="noreferrer"
              className="link-underline text-white"
            >
              Google API Services User Data Policy
            </a>
            , including the <b>Limited Use</b> requirements.
          </p>
          <ul className="list-disc ps-6 space-y-2 text-white/75">
            <li>
              We request <b>only</b> the <span className="font-mono">gmail.send</span> scope. We do
              <b> not</b> read, list, modify, delete, label, archive, or otherwise access your mail
              content.
            </li>
            <li>
              Access and refresh tokens are stored encrypted at rest in MongoDB Atlas and are never
              shared with third parties.
            </li>
            <li>Tokens travel over TLS 1.2+ server-to-server only; never exposed to the browser.</li>
            <li>
              You may disconnect at any time via <b>Profile → Connected Accounts → Disconnect Gmail</b>,
              or via your{" "}
              <a
                href="https://myaccount.google.com/permissions"
                target="_blank"
                rel="noreferrer"
                className="link-underline text-white"
              >
                Google Account → Security → Third-party access
              </a>
              . Revocation is instant.
            </li>
            <li>We do not use Gmail data to train machine-learning models.</li>
            <li>We do not sell, rent, or trade Gmail-derived data.</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="font-display font-bold text-2xl">6. Retention</h2>
          <p className="text-white/75">
            We retain your data only as long as your account is active. On deletion, all profile,
            documents, applications, and saved-search records are removed within 30 days. Backups
            are purged within 90 days. Anonymized aggregate metrics (e.g., total jobs applied
            platform-wide) may be retained.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display font-bold text-2xl">7. Contact</h2>
          <p className="text-white/75">
            Data Protection Officer: <a href="mailto:dpo@maqam-ksa.com" className="link-underline text-white">dpo@maqam-ksa.com</a><br/>
            Postal: Riyadh, Kingdom of Saudi Arabia.<br/>
            Regulator: Saudi Data and AI Authority (SDAIA) — <a href="https://sdaia.gov.sa" target="_blank" rel="noreferrer" className="link-underline text-white">sdaia.gov.sa</a>.
          </p>
        </section>

        <footer className="border-t border-white/10 pt-8 text-xs text-white/40">
          © 2026 Maqam. All rights reserved. — Maqam™ is a registered trademark in the Kingdom of Saudi Arabia.
        </footer>
      </main>
    </div>
  );
}
